'use client';

import { isDomain } from '@schuelerhilfe/shared/src/enums/domain';
import {
  magazinePath,
  magazinePathWithoutLocale,
  type MagazineRoute,
} from '@schuelerhilfe/shared/src/seo/magazinePaths';
import { useLocale } from 'next-intl';
import { useCallback } from 'react';
import { useOptionalRenderContext } from '@/lib/render/RenderContextProvider';

const DOMAIN_TAG_PREFIX = 'domain:';

/**
 * The **only** place a magazine href is built on the client.
 *
 * Brand and locale both matter: the magazine is the one URL family whose path
 * differs per brand *and* per locale (spec 10.01.02 § 2.4), so a card rendered
 * on fit4school.ch must link to `/blog/artikel/…` and a French one to
 * `/fr/blog/article/…`. The brand comes off the render context's `domain:*`
 * tag — the same source the load-more call uses — and the locale from next-intl.
 *
 * `getActiveLocale()` in `lib/domain-config.ts` hard-returns `'de'`, so neither
 * client nor server code may use it for a magazine path: a CH French request
 * would silently be given the German one.
 *
 * The returned href is **locale-prefix free**, because every caller renders it
 * through `@/components/Link` → next-intl, which runs `localePrefix:
 * 'as-needed'` and prepends `/fr` itself. The map's `fr` entries carry their own
 * `/fr/` (they are full URLs, for canonicals and the sitemap), so handing one
 * to `Link` unstripped emits `/fr/fr/blog/…`. Server-side consumers that need
 * the full URL keep calling `magazinePath()` directly.
 *
 * Returns `undefined` when the brand serves no such page type in this locale
 * (CH has no category URL, no French author page). Callers render the content
 * without a link rather than emitting an href that 404s.
 */
export function useMagazineHref(): (
  route: MagazineRoute,
  slug?: string,
) => string | undefined {
  const ctx = useOptionalRenderContext();
  const locale = useLocale();
  const domainTag = ctx?.tagBag.find((tag) =>
    tag.startsWith(DOMAIN_TAG_PREFIX),
  );
  const domain = domainTag?.slice(DOMAIN_TAG_PREFIX.length);

  return useCallback(
    (route: MagazineRoute, slug?: string) => {
      if (!domain || !isDomain(domain)) {
        return undefined;
      }
      const path = magazinePath(domain, locale, route, slug);
      return path ? magazinePathWithoutLocale(path, locale) : undefined;
    },
    [domain, locale],
  );
}
